import { useCallback, useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useMount } from "app/custom-hooks/useMount";
import { useUnmount } from "app/custom-hooks/useUnmount";
import type { CrmColumnItem } from "app/pages/sales-intelligence/sub-modules/management/types";

import { resetJobState, setJobState, setSelectedCountryId } from "../../store/action-creators";
import { selectSelectedCountryId } from "../../store/selectors";
import type { CrmEnrichmentJobDTO } from "../../types/types";
import { getDefaultCountryId } from "../../utils/job";
import useFiltersActions from "../useFiltersActions";

type HookParams = {
    initialJob?: CrmEnrichmentJobDTO;
    initialColumns: readonly CrmColumnItem[];
    columns: readonly CrmColumnItem[];
};

type HookValue = {
    initializeFiltersFromJob: (job?: CrmEnrichmentJobDTO) => void;
};

const useCrmEnrichmentJob = ({ columns, initialJob, initialColumns }: HookParams): HookValue => {
    const dispatch = useDispatch();

    const currentCountryId = useSelector(selectSelectedCountryId);

    const { initializeFromJob } = useFiltersActions(columns);

    const handleInitializeFiltersFromJob = useCallback(
        (job?: CrmEnrichmentJobDTO) => {
            if (!job?.filters) {
                return;
            }

            initializeFromJob(job.filters, initialColumns);
        },
        [initializeFromJob, initialColumns],
    );

    useMount(() => {
        if (!currentCountryId) {
            dispatch(setSelectedCountryId(getDefaultCountryId()));
        }
    });

    useEffect(() => {
        if (initialJob) {
            dispatch(setJobState(initialJob));
        }
    }, [initialJob]);

    useEffect(() => {
        if (initialJob?.filters && columns.length > 0) {
            handleInitializeFiltersFromJob(initialJob);
        }
    }, [initialJob?.filters, columns, handleInitializeFiltersFromJob, initialJob]);

    useUnmount(() => {
        dispatch(resetJobState());
    });

    return {
        initializeFiltersFromJob: handleInitializeFiltersFromJob,
    };
};

export default useCrmEnrichmentJob;
